import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import type {
  ApiEnvelope,
  FacebookPost,
  AlbumSummary,
  AlbumDetail,
  ContactMessage,
} from '../types';

export type ApiCallback<T> = (response: AxiosResponse<T>) => void;

export class ApiClient {
  protected requestInterceptor:
    | ((config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig)
    | null = null;
  protected service: AxiosInstance;

  constructor(baseUrl: string) {
    const service = axios.create({
      baseURL: baseUrl,
    });
    if (this.requestInterceptor) {
      service.interceptors.request.use(this.requestInterceptor);
    }
    service.interceptors.response.use(this.handleSuccess, this.handleError);
    this.service = service;
  }

  getCookieValue(name: string): string {
    const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return match ? (match.pop() as string) : '';
  }

  protected handleSuccess(response: AxiosResponse) {
    return response;
  }

  protected handleError = (error: unknown) => {
    return Promise.reject(error);
  };

  get<T>(path: string, callback: ApiCallback<T>) {
    return this.service
      .get<T>(path, {
        validateStatus: (status) => status < 500, // Reject if status code is greater than or equal to 500
      })
      .then((response) => callback(response));
  }

  put<T>(path: string, payload: unknown, callback: ApiCallback<T>) {
    return this.service
      .request<T>({
        method: 'PUT',
        url: path,
        responseType: 'json',
        headers: {
          'Content-Type': 'application/json',
        },
        data: payload,
      })
      .then((response) => callback(response));
  }

  post<T>(path: string, payload: unknown, callback: ApiCallback<T>) {
    return this.service
      .request<T>({
        method: 'POST',
        url: path,
        responseType: 'json',
        headers: {
          'Content-Type': 'application/json',
        },
        data: payload,
      })
      .then((response) => callback(response));
  }

  delete<T>(path: string, payload: unknown, callback: ApiCallback<T>) {
    return this.service
      .request<T>({
        method: 'DELETE',
        url: path,
        responseType: 'json',
        data: payload,
      })
      .then((response) => callback(response));
  }

  uploadFile<T>(
    scope: string,
    resourceId: string | number,
    files: FileList | File[],
    callback: ApiCallback<T>,
  ) {
    const formData = new FormData();
    formData.append('scope', scope);
    formData.append('resource_id', String(resourceId));
    for (let i = 0; i < files.length; i++) {
      formData.append('files[' + i + ']', files[i]);
    }
    return this.service
      .request<T>({
        method: 'POST',
        url: 'file/upload',
        responseType: 'json',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => callback(response));
  }

  /**
   * Restituisce gli ultimi post della pagina Facebook
   */
  getPosts(callback: ApiCallback<ApiEnvelope<FacebookPost[]>>) {
    return this.get('/posts', callback);
  }

  /**
   * Restituisce l'elenco degli album della pagina Facebook
   */
  getAlbums(callback: ApiCallback<ApiEnvelope<AlbumSummary[]>>) {
    return this.get('/albums', callback);
  }

  /**
   * Restituisce l'album con l'id specificato dalla pagina Facebook
   */
  getAlbum(id: string | number, callback: ApiCallback<ApiEnvelope<AlbumDetail>>) {
    return this.get('/albums/' + id, callback);
  }

  /**
   * Invia un messaggio all'indirizzo email del gestore
   */
  sendMessage(data: ContactMessage, callback: ApiCallback<unknown>) {
    return this.post('/send-message', data, callback);
  }
}

export const api = new ApiClient('/api/');
